import React, {useEffect, useRef, useState} from "react";
import type {ContextProps} from '../../types';
import {PageTitle} from "../../Components/Landmarks";
import {LayoutFullBleed} from "../../Components/Layout";
import {DialogBox} from "../../Components/Dialog/DialogBox";
import {emptyStudentQueueModel} from "./libraryFactory";
import {getFieldArrayFromObjectArrayByField, isArrayNullOrEmpty} from "../../Utilities/Types/arrayUtilities";
import {libraryQueueApi} from "./libraryQueueApi";
import {locations} from "../../Utilities/Location";
import {LibraryQueueForm} from "./LibraryQueueForm";
import {useInputForm} from "../../Components/Inputs";
import {orderRequestApi} from "../Order/OrderRequest/orderRequestApi";
import {createListFromArrayOfObjects} from "../../Utilities/Types/listUtilities";
import type {ListItem} from "../../Utilities/Types/listUtilities";
import {LibraryQueueList} from "./LibraryQueueList";
import {buildDuplicateWarningMessage, getTerminalOrderStatusTextByValue, isTerminalOrderStatus} from "./libraryUtility";
import {STUDENT_APPROVAL_STATUS, STUDENT_STATUS} from "../Student/studentConstants";
import {isStartBeforeEnd} from "../../Utilities/Types/dateUtilities";
import {error as notifyError} from "../../Components/Notifications/NotifyUser/notifyUserUtility";
import {ButtonLinkStyle} from "../../Components/Inputs";
import {isFimcAdmin} from "@utilities/LocalStorage/storageUtilities";

export const LibraryQueueContainer = ({
                                          handleApiCall,
                                          redirect,
                                          systemType,
                                          systemTypeText,
                                          userId,
                                      }: ContextProps) => {

    const [queue, setQueue] = useState<Record<string, unknown>[]>([]);
    const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set([]));
    const [students, setStudents] = useState<ListItem[]>();
    const [shippingLocations, setShippingLocations] = useState<ListItem[]>();

    const [isDuplicateDialogOpen, setIsDuplicateDialogOpen] = useState(false);
    const duplicateWarningMessage = useRef("");
    const pendingQueueAssignment = useRef<Record<string, unknown> | null>(null);

    const loanForm = useInputForm(emptyStudentQueueModel);
    const pendingQueue = queue.filter((q: Record<string, unknown>) => q.student === null || q.shippingLocation === null);
    const isPendingQueueEmpty = isArrayNullOrEmpty(pendingQueue);
    const processingQueue = queue.filter((q: Record<string, unknown>) => q.student !== null && q.shippingLocation !== null);

    const handleSelectAll = () => {
        const queueItems = getFieldArrayFromObjectArrayByField(pendingQueue, "queueItemId");
        setSelectedItems(new Set(queueItems.map((id: unknown) => String(id))));
    }

    const handleDeselectAll = () => {
        setSelectedItems(new Set([]));
    }

    const handleSelectedItemsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const item = event.target.value;
        const updatedSet = new Set(selectedItems || []);
        
        updatedSet.has(item)
            ? updatedSet.delete(item)
            : updatedSet.add(item);
        
        setSelectedItems(updatedSet);
    }

    const handleViewOrder = (orderId: string, catalogItemId: string) => redirect(locations.ORDER_VIEW.getLink([orderId, catalogItemId]).to);

    const handleGetQueue = () => {
        return handleApiCall(() => libraryQueueApi.getQueue(systemType), (results: Record<string, unknown>[]) => setQueue(results));
    }

    const handleRemoveFromQueue = (queuedItemId: string, orderStatus?: string) => {
        let message = `This will PERMANENTLY remove your request from the Queue. Are you sure?`;

        if (orderStatus && isTerminalOrderStatus(Number(orderStatus))) {
            const statusDisplay = getTerminalOrderStatusTextByValue(orderStatus);
            message = `This queue item has an order with a status of ${statusDisplay}. ` + message;
        }

        if (confirm(message)) {
            handleApiCall(() => libraryQueueApi.removeFromQueue(queuedItemId), handleGetQueue);
        }
    }

    const handleAssignQueueItemsToStudent = async (studentQueueViewModel: Record<string, unknown>, confirmDuplicateQueueItems: boolean) => {
        studentQueueViewModel.confirmDuplicateQueueItems = confirmDuplicateQueueItems;
        await handleApiCall(() => libraryQueueApi.assignQueueItems(studentQueueViewModel));
        await handleGetQueue();
        setSelectedItems(new Set([]));
    }

    const handleCloseDuplicateDialog = () => {
        setIsDuplicateDialogOpen(false);

        duplicateWarningMessage.current = "";
        pendingQueueAssignment.current = null;
    }

    const handleConfirmDuplicateDialog = async () => {
        if (!pendingQueueAssignment.current) {
            handleCloseDuplicateDialog();
            return;
        }

        await handleAssignQueueItemsToStudent(pendingQueueAssignment.current, true);
        handleCloseDuplicateDialog();
    }

    const handleValidateDuplicateQueueItemsForStudentAndAssignQueueItems = (studentQueueViewModel: Record<string, unknown>) => {
        handleApiCall(
            () => libraryQueueApi.ValidateDuplicateTextbookQueueItemsForStudent(studentQueueViewModel),
            async (validationResult: Record<string, unknown>) => {
                const hasDuplicates = Boolean(validationResult.hasDuplicates);

                if (hasDuplicates) {
                    duplicateWarningMessage.current = buildDuplicateWarningMessage(validationResult);

                    pendingQueueAssignment.current = { ...studentQueueViewModel };
                    setIsDuplicateDialogOpen(true);
                    return;
                }

                handleAssignQueueItemsToStudent(studentQueueViewModel, false);
            }
        );
    }

    const handleAssignQueueItems = async (studentQueueViewModel: Record<string, unknown>) => {
        const hasDateRange = studentQueueViewModel.needFrom && studentQueueViewModel.needTo;
        const isInvalidDateRange = hasDateRange && !isStartBeforeEnd(String(studentQueueViewModel.needFrom), String(studentQueueViewModel.needTo));

        if (isInvalidDateRange) {
            const validationMessage = "Need From date must be before Need To date.";
            loanForm.needFrom.setError("Need From date must be before Need To date.");
            loanForm.needTo.setError("Need To date must be after Need From date.");
            notifyError(validationMessage);
            return;
        }

        studentQueueViewModel.queueItems = Array.from(selectedItems);

        if ((studentQueueViewModel.queueItems as unknown[]).length === 0) {
            notifyError("Please select at least one item from the queue to assign to a student.");
            return;
        }

        handleValidateDuplicateQueueItemsForStudentAndAssignQueueItems(studentQueueViewModel)
    }

    const handleExportPendingQueue = () => {
        handleApiCall(() => libraryQueueApi.getPendingTextbookQueueReport());
    };

    useEffect(() => {

        const getStudents = () => {
            handleApiCall(() => orderRequestApi.getStudents({
                userId: userId,
                isActive: true,
                studentApprovalStatus: STUDENT_APPROVAL_STATUS.Approved.id,
                studentStatus: STUDENT_STATUS.Active.id,
                forDropDown: true,
            }), (results: Record<string, unknown>[]) => {
                setStudents(createListFromArrayOfObjects(results, `studentId`, `fullName`));
            });
        };

        const getShippingLocations = () => {
            handleApiCall(() => orderRequestApi.getShippingLocations(), (results: Record<string, unknown>[]) => {
                setShippingLocations(createListFromArrayOfObjects(results, `shippingLocationId`, `name`));
            });
        }

        if (!shippingLocations)
            getShippingLocations();
        if (!students)
            getStudents();

        handleGetQueue();
    }, []);

    if (!shippingLocations || !students || !loanForm) return;

    return <>
        <PageTitle h1={`${systemTypeText} Queue`}/>

        <LayoutFullBleed className={`catalog`}>
            <p>If an item is not checked out within 48 hours of being  added to your queue, the item will be removed.</p>
            {
                isFimcAdmin() &&
                <ButtonLinkStyle
                    isCollapsed
                    label="Export Pending Queue"
                    name="btnExportPendingQueue"
                    onClick={handleExportPendingQueue}
                />
            }
            {
                pendingQueue &&
                <LibraryQueueList
                    handleDeselectAll={handleDeselectAll}
                    handleRemoveFromQueue={handleRemoveFromQueue}
                    handleSelectAll={handleSelectAll}
                    handleSelectedItemsChange={handleSelectedItemsChange}
                    queue={pendingQueue}
                    selectedItems={selectedItems}
                />
            }

            {
                !isPendingQueueEmpty &&
                <LibraryQueueForm
                    handleAssignQueueItems={handleAssignQueueItems}
                    loanForm={loanForm}
                    shippingLocations={shippingLocations}
                    students={students}
                />
            }
            {
                processingQueue &&
                <LibraryQueueList
                    handleRemoveFromQueue={handleRemoveFromQueue}
                    handleViewOrder={handleViewOrder}
                    heading={`Processing`}
                    queue={processingQueue}
                />
            }
        </LayoutFullBleed>

        <DialogBox
            handleCancel={handleCloseDuplicateDialog}
            handleSave={handleConfirmDuplicateDialog}
            openDialog={isDuplicateDialogOpen}
            title="Duplicate Textbook Request"
            saveLabel="Continue"
            cancelLabel="Cancel">
            <div className="callout warning">
                <p style={{whiteSpace: "pre-wrap", marginBottom: 0}}>{duplicateWarningMessage.current}</p>
            </div>
        </DialogBox>
    </>;
};

